import React from 'react'
import { Button as ButtonComponent } from '@/components/ui/button'
import {
    HALTED_NOTE,
    MODE_HINTS,
    NOT_OFFERED_NOTE,
    READ_ONLY_NOTE,
    STATUS_TITLES,
    TOO_MUCH_DATA_NOTE,
    actionLabel,
    effectiveModeNote,
    modeChoices,
    panelVisible,
    reasonText,
    statusText,
    unconfirmedBanner,
    visibleActions,
} from '@/utils/budget-optimizer'
import { useBudgetOptimizer } from './hooks/useBudgetOptimizer'

const Button = ButtonComponent as React.ComponentType<any>

interface BudgetOptimizerPanelProps {
    adCampaignId: string
    /** Formats a minor-unit amount. */
    formatAmount: (minor: number) => string
    /** Do not read the optimizer (campaigns it can never handle). */
    skip?: boolean
}

const formatWhen = (value?: string | null) => (value ? new Date(value).toLocaleString() : '')

/**
 * The budget optimizer of a campaign: its mode, the suggested or made move and what the owner can do about it.
 * Every button comes from the server's `actions` list; only the owner sees any, a superadmin reads only. Shown when
 * the optimizer is offered, and also when it is not any more but a move is open or can still be undone.
 */
const BudgetOptimizerPanel: React.FC<BudgetOptimizerPanelProps> = ({ adCampaignId, formatAmount, skip = false }) => {
    const { optimizer, busy, changeMode, act } = useBudgetOptimizer(adCampaignId, { skip })

    if (!optimizer || !panelVisible(optimizer)) return null

    const decision = optimizer.decision
    const choices = modeChoices(optimizer)
    const actions = visibleActions(optimizer)
    const banner = unconfirmedBanner(decision, formatAmount)
    const modeNote = effectiveModeNote(optimizer)
    const currencyAmount = decision?.amount != null ? formatAmount(decision.amount) : ''

    return (
        <section
            aria-label="Budget optimizer"
            data-testid="budget-optimizer-panel"
            className="tw:sticky tw:left-0 tw:w-[1000px] tw:border-b tw:border-[#2B3B4C] tw:bg-[#0B1E33] tw:px-6 tw:py-3 tw:text-sm tw:text-white"
        >
            <div className="tw:flex tw:flex-wrap tw:items-center tw:gap-3">
                <span className="tw:font-semibold">Budget optimizer</span>
                {choices.length > 0 ? (
                    <div role="radiogroup" aria-label="Optimizer mode" className="tw:flex tw:gap-2">
                        {choices.map((choice) => (
                            <button
                                key={choice.mode}
                                type="button"
                                role="radio"
                                aria-checked={optimizer.mode === choice.mode}
                                disabled={busy || choice.disabled}
                                onClick={() => optimizer.mode !== choice.mode && void changeMode(choice.mode)}
                                className={
                                    optimizer.mode === choice.mode
                                        ? 'tw:rounded tw:bg-[#3871FF] tw:px-3 tw:py-1'
                                        : 'tw:rounded tw:border tw:border-[#3B5168] tw:px-3 tw:py-1 tw:disabled:opacity-50'
                                }
                            >
                                {choice.label}
                            </button>
                        ))}
                    </div>
                ) : (
                    <span data-testid="budget-optimizer-mode">Mode: {optimizer.mode}</span>
                )}
            </div>

            {choices.length > 0 && (
                <p className="tw:!m-0 tw:mt-1 tw:text-xs tw:text-gray-200" data-testid="budget-optimizer-hint">
                    {MODE_HINTS[optimizer.mode as keyof typeof MODE_HINTS]}
                </p>
            )}
            {!optimizer.isOwner && (
                <p className="tw:!m-0 tw:mt-1 tw:text-xs tw:text-gray-200" data-testid="budget-optimizer-read-only">
                    {READ_ONLY_NOTE}
                </p>
            )}
            {!optimizer.available && (
                <p className="tw:!m-0 tw:mt-1 tw:text-xs tw:text-yellow-300" data-testid="budget-optimizer-unavailable">
                    {NOT_OFFERED_NOTE}
                </p>
            )}
            {optimizer.halted && (
                <p className="tw:!m-0 tw:mt-1 tw:text-xs tw:text-yellow-300" data-testid="budget-optimizer-halted">
                    {HALTED_NOTE}
                </p>
            )}
            {modeNote && (
                <p
                    className="tw:!m-0 tw:mt-1 tw:text-xs tw:text-yellow-300"
                    data-testid="budget-optimizer-effective-mode"
                >
                    {modeNote}
                </p>
            )}
            {optimizer.tooMuchData && (
                <p
                    className="tw:!m-0 tw:mt-1 tw:text-xs tw:text-yellow-300"
                    data-testid="budget-optimizer-too-much-data"
                >
                    {TOO_MUCH_DATA_NOTE}
                </p>
            )}

            {banner && (
                <div
                    role="status"
                    className="tw:mt-2 tw:rounded tw:border tw:border-yellow-300 tw:px-3 tw:py-2 tw:text-yellow-300"
                    data-testid="budget-optimizer-banner"
                >
                    {banner}
                </div>
            )}

            {decision && (
                <div className="tw:mt-2" data-testid="budget-optimizer-decision">
                    <h3 className="tw:!m-0 tw:!mb-1 tw:!text-sm tw:!font-semibold">
                        {STATUS_TITLES[decision.status as keyof typeof STATUS_TITLES] || 'Budget move'}
                    </h3>
                    {decision.donor && decision.receiver && (
                        <p className="tw:!m-0" data-testid="budget-optimizer-move">
                            {`Move ${currencyAmount} a day from ${decision.donor.adSetName || 'an ad set'} (${formatAmount(
                                decision.donor.budgetBefore,
                            )} to ${formatAmount(decision.donor.budgetAfter)}) to ${
                                decision.receiver.adSetName || 'another ad set'
                            } (${formatAmount(decision.receiver.budgetBefore)} to ${formatAmount(
                                decision.receiver.budgetAfter,
                            )}).`}
                        </p>
                    )}
                    {decision.status === 'proposed' && decision.expiresAt && (
                        <p className="tw:!m-0 tw:text-xs tw:text-gray-200">{`Expires ${formatWhen(decision.expiresAt)}.`}</p>
                    )}
                    {decision.status === 'proposed' && optimizer.effectiveModeReason === 'automated_unavailable' && (
                        <p className="tw:!m-0 tw:text-xs tw:text-gray-200">
                            Suggested for you to apply: automated mode is not available.
                        </p>
                    )}
                    {statusText(decision) && (
                        <p className="tw:!m-0 tw:text-xs tw:text-gray-200" data-testid="budget-optimizer-status">
                            {statusText(decision)}
                        </p>
                    )}
                    {decision.lastReason && (
                        <p className="tw:!m-0 tw:text-xs tw:text-gray-200">{reasonText(decision.lastReason)}</p>
                    )}
                    {actions.length > 0 && (
                        <div className="tw:mt-2 tw:flex tw:flex-wrap tw:gap-2">
                            {actions.map((action) => (
                                <Button
                                    key={action}
                                    size="sm"
                                    variant={action === 'dismiss' || action === 'check_again' ? 'secondary' : undefined}
                                    disabled={busy}
                                    onClick={() => void act(action, decision.id)}
                                >
                                    {actionLabel(action, decision)}
                                </Button>
                            ))}
                        </div>
                    )}
                </div>
            )}

            {!decision && optimizer.available && optimizer.mode !== 'off' && optimizer.lastEvaluatedAt && (
                <p className="tw:!m-0 tw:mt-2 tw:text-xs tw:text-gray-200" data-testid="budget-optimizer-last">
                    {`Last checked ${formatWhen(optimizer.lastEvaluatedAt)}.`}
                    {optimizer.lastReasons?.length > 0 ? ` ${optimizer.lastReasons.map(reasonText).join(' ')}` : ''}
                </p>
            )}
        </section>
    )
}

export default BudgetOptimizerPanel
